import { Injectable, inject } from '@angular/core';
import { MutationOptions, QueryOptions } from '@apollo/client/core';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, Observable, ReplaySubject, map, tap } from 'rxjs';
import { QUERY_MAIN_MENU, QUERY_MENU_STATE, UPSERT_MENU_STATE } from './nav-menu-queries';
import { IClsMenuItem } from './nav-menu-schema';
import { HttpClient } from '@angular/common/http';
import { ITpUserSettings, SETTINGS_TYPE } from 'ngx-register-base';
import { noCacheQuery } from '../../../utils/graphql.functions';

@Injectable({ providedIn: 'root' })
export class SmaPageMenuStore {
  public mainMenu$ = new ReplaySubject<IClsMenuItem[]>();
  private _menuStateSettings$ = new BehaviorSubject<ITpUserSettings | undefined>(undefined);

  private readonly _httpClient = inject(HttpClient);
  protected apollo = inject(Apollo);

  readonly fetchMenu = (): Observable<{ readonly data?: { usr_cls_menu_item: any } }> => {
    const options: QueryOptions = {
      query: QUERY_MAIN_MENU,
      fetchPolicy: 'no-cache',
      variables: {
        where: { is_deleted: { _eq: false } },
      },
    };

    return this.apollo
      .query<{ usr_cls_menu_item: IClsMenuItem[] }>(options)
      .pipe(tap(({ data: { usr_cls_menu_item: menu } }) => this.mainMenu$.next(menu)));
  };

  public upsertMenuState(menuState: boolean): void {
    const mo: MutationOptions = {
      mutation: UPSERT_MENU_STATE,
      variables: {
        setting: {
          id: this.menuStateSettingId,
          name: 'menu-state',
          module_name: 'any',
          settings: {
            menuState,
          },
          settings_type: SETTINGS_TYPE.PAGE_MENU_STATE,
        },
        onConflict: {
          constraint: 'tp_user_settings_pkey',
          update_columns: ['name', 'settings'],
        },
      },
    };

    this.apollo
      .mutate<{ insert_usr_tp_user_settings_one: ITpUserSettings }>(mo)
      .pipe(map(({ data }) => data?.insert_usr_tp_user_settings_one))
      .subscribe((settings) => {
        this.menuStateSetting = settings;
      });
  }

  public queryMenuState(userId: string): void {
    const qo: QueryOptions = noCacheQuery({
      query: QUERY_MENU_STATE,
      variables: { user_id: userId },
    });

    this.apollo.query<{ usr_tp_user_settings: ITpUserSettings[] }>(qo).subscribe((response) => {
      const [setting] = response.data.usr_tp_user_settings;
      this.menuStateSetting = setting;
    });
  }

  public set menuStateSetting(settings: ITpUserSettings | undefined) {
    this._menuStateSettings$.next(settings);
  }

  public get menuStateSetting(): ITpUserSettings | undefined {
    return this._menuStateSettings$.getValue();
  }

  public get menuStateSettings$(): Observable<ITpUserSettings | undefined> {
    return this._menuStateSettings$.asObservable();
  }

  public get menuStateSettingId(): string | undefined {
    return this.menuStateSetting?.id;
  }
}
